"use client";

import CircularButton from "@/components/CircularButton";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import {
    queryEditableFlashcardCard,
    queryTermTextArea,
} from "@/components/flashcards/flashcardsCard/EditableFlashcardCard/utils";
import BorderColoredInput from "@/components/styled/input/BorderColoredInput";
import { borderRadius } from "@/utils/borderRadius";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { Flashcard } from "@knowt/syncing/graphql/schema";
import { range } from "lodash-es";
import { Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { useOnTabPressOnLastFocusedFlashcardCard } from "../hooks/useOnTabPressOnLastFocusedFlashcardCard";
import { useSetEditingContextSelector } from "../hooks/useSetEditingContext";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import Image from "next/image";
import { ASSETS_URL } from "@/config/deployConstants";
import clsx from "clsx";
import br from "@/styles/breakpoints.module.css";
import LazyLoaded from "@/components/LazyLoaded";
import dynamic from "next/dynamic";
import toast from "react-hot-toast";

const ImportFromQuizletPopup = dynamic(() => import("@/components/Popup/ImportFromQuizletPopup"));

const StartAddFirstFlashcard = ({ onClick }) => {
    return (
        <FlexRowAlignCenter style={{ cursor: "pointer", maxWidth: "60rem", margin: "5rem auto" }} onClick={onClick}>
            <CircularButton radius="7rem" sx={{ backgroundColor: themeColors.neutral1 }}>
                <Plus size={iconSizes.LG} color={themeColors.neutral3} />
            </CircularButton>
            <FlexColumn style={{ marginLeft: "1.5rem" }}>
                <span className="bold" style={{ fontSize: "2.5rem" }}>
                    Click the + to start adding flashcards!
                </span>
                <span style={{ fontSize: "1.6rem", marginTop: "1rem" }}>
                    You can create your own flashcards really easily. Just click the plus and we'll even autofill
                    definitions for you.
                </span>
            </FlexColumn>
        </FlexRowAlignCenter>
    );
};

const SetEditingTermsAdder = () => {
    const [cardsToAdd, setCardsToAdd] = useState(1);
    const addFlashcard = useSetEditingContextSelector(s => s.addFlashcard);
    const addFlashcards = useSetEditingContextSelector(s => s.addFlashcards);
    const flashcardSet = useSetEditingContextSelector(s => s.flashcardSet);
    const virtuosoRef = useSetEditingContextSelector(s => s.virtuosoRef);
    const flashcards = flashcardSet?.flashcards;
    const numOfFlashcards = flashcards?.length || 0;

    const [isImportFlashcardsPopupOpen, setIsImportFlashcardsPopupOpen] = useState(false);

    const handleAddNewTerm = useCallback(
        async (n = 1) => {
            let createdFlashcard: Flashcard | undefined;

            for (const _ of range(0, n)) {
                createdFlashcard = await addFlashcard();
            }

            if (!createdFlashcard) return;
            const newFlashcard = createdFlashcard as Flashcard;

            setTimeout(() => {
                const newFlashcardCard = queryEditableFlashcardCard(newFlashcard.flashcardId);

                virtuosoRef?.current?.scrollToIndex({ index: numOfFlashcards, behavior: "smooth" });
                if (newFlashcardCard) {
                    setTimeout(() => queryTermTextArea(newFlashcardCard)?.focus?.(), 100);
                }
            }, 25);

            return createdFlashcard.flashcardId;
        },
        [addFlashcard, virtuosoRef, numOfFlashcards]
    );

    useOnTabPressOnLastFocusedFlashcardCard((flashcards?.[numOfFlashcards - 1] as Flashcard)?.flashcardId, async () => {
        const id = await handleAddNewTerm(1);
        return id || null;
    });

    if (!numOfFlashcards) {
        return <StartAddFirstFlashcard onClick={() => handleAddNewTerm(1)} />;
    }

    return (
        <FlexRowAlignCenter
            style={{
                gap: spacing.MD,
                justifyContent: "center",
                backgroundColor: themeColors.neutralWhite,
                borderRadius: "2rem",
                padding: "2.4rem 3rem",
                margin: "3.2rem 0",
            }}>
            <CircularRectTextButton
                tooltip="Add from Quizlet or CSV"
                onClick={() => setIsImportFlashcardsPopupOpen(true)}
                style={{
                    gap: "0.8rem",
                    padding: `${spacing.XS} ${spacing.SM}`,
                    border: `1px solid ${themeColors.neutral1}`,
                    height: "4rem",
                }}>
                <Image src={`${ASSETS_URL}/images/quizlet-icon.svg`} alt={"quizlet"} width={16} height={16} />
                <Image src={`${ASSETS_URL}/images/import-icon.svg`} alt={"import"} width={16} height={16} />
                <span className={clsx("secondaryTextBold1", br.mdDownDisplayNone)}>Add from Quizlet or CSV</span>
            </CircularRectTextButton>
            <CircularRectTextButton
                style={{
                    backgroundColor: themeColors.neutralBlack,
                    height: "4rem",
                    padding: `1rem ${spacing.SM}`,
                    gap: spacing.XS,
                }}
                onClick={() => handleAddNewTerm(cardsToAdd)}>
                <Plus size={iconSizes.MD} color={themeColors.neutralWhite} />
                <p className="secondaryTextBold1" style={{ color: themeColors.neutralWhite }}>
                    Add card(s)
                </p>
            </CircularRectTextButton>
            <BorderColoredInput
                className={"bodyBold1"}
                type="number"
                defaultValue={1}
                onChange={e => setCardsToAdd(Number(e.target.value))}
                style={{
                    backgroundColor: themeColors.background,
                    borderRadius: borderRadius.shortInput,
                    paddingTop: spacing.XS,
                    paddingBottom: spacing.XS,
                    width: "8rem",
                    height: "4rem",
                    border: "none",
                }}
            />

            <LazyLoaded load={isImportFlashcardsPopupOpen}>
                <ImportFromQuizletPopup
                    isOpen={isImportFlashcardsPopupOpen}
                    onClose={() => setIsImportFlashcardsPopupOpen(false)}
                    bypassChromeExtensionPopup
                    onImportClick={async ({ flashcards }) => {
                        try {
                            toast.loading("Importing flashcards...", { id: "import-flashcards" });
                            await addFlashcards({ flashcards });
                            setIsImportFlashcardsPopupOpen(false);
                            toast.success("Flashcards imported successfully", { id: "import-flashcards" });
                        } catch {
                            toast.error("Error importing flashcards, try again", { id: "import-flashcards" });
                        }
                    }}
                />
            </LazyLoaded>
        </FlexRowAlignCenter>
    );
};

export default SetEditingTermsAdder;
